import { useCallback, useEffect, useState } from 'react'
import { Link } from '@tanstack/react-router'

import {
  LedgerBadge,
  LedgerTable,
  type LedgerBadgeTone,
  type LedgerColumn,
  type LedgerServerQuery,
} from '@/components/ui/LedgerTable'
import { AssignUserDialog } from '@/components/AssignUserDialog'
import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import { PanelNotes } from '@/components/PanelNotes'
import type {
  PhotoNoteReplace,
  PhotoNoteSubmit,
} from '@/components/PhotoNoteComposer'
import { FollowUpActionDialog } from '@/components/FollowUpActionDialog'
import { NewFollowUpDialog } from '@/components/NewFollowUpDialog'
import {
  describeSchedule,
  dueState,
  remainingSchedule,
  type DueState,
  type FollowUpServiceType,
  type NewFollowUpInput,
  type ScheduleStep,
} from '@/db/domain/followUps'
import {
  formatBookedLabel,
  fromUtcIsoToZaParts,
  toZaDateKey,
} from '@/lib/datetime'
import { initials } from '@/lib/initials'

/** A customer note that answered this follow-up, ready for display. */
export interface FollowUpLinkedNote {
  id: string
  text: string
  authorName: string
  createdAt: string
}

/** One follow-up, flattened for display: no ids the reader would have to resolve. */
export interface FollowUpRow {
  id: string
  customerId: string
  /** Public-facing id for customer routes; falls back to customerId. */
  customerPublicId?: string
  customerName: string
  customerPhone?: string
  customerEmail?: string
  assigneeId?: string
  assigneeName?: string
  /** Who created the follow-up, resolved for display. */
  createdByName?: string
  createdAt?: string
  categoryId?: string
  categoryName?: string
  title: string
  details?: string
  dueAt: string
  schedule: ScheduleStep[]
  /** Which rung a `done` would use next — decides whether it re-arms or ends. */
  scheduleIndex: number
  status: 'open' | 'closed'
  service?: { type: FollowUpServiceType; id: string; label: string }
  lastDoneAt?: string
  /** Customer notes tagged with this follow-up, newest first. */
  notes?: FollowUpLinkedNote[]
  /** The full thread for the side panel, tombstones included, oldest first. */
  feed?: CalloutNotesSectionNote[]
}

export type FollowUpsTab = 'due' | 'upcoming' | 'archive'

/** A tab whose pages come from the server-side follow-ups search. */
export type FollowUpsServerScope = Exclude<FollowUpsTab, 'due'>

/**
 * Server-driven paging for the Upcoming and Archive tabs. The keys are the
 * current page's membership + order; the matching records arrive via `rows`
 * (the search upserts them into the follow-ups collection).
 */
export interface FollowUpsServerTab {
  keys: string[]
  totalItems: number
  loading?: boolean
  error?: boolean
  onQueryChange: (scope: FollowUpsServerScope, query: LedgerServerQuery) => void
}

export interface FollowUpsScreenProps {
  rows: FollowUpRow[]
  /**
   * Which tab shows: what needs acting on now, or what is still coming. When
   * set with `onSelectTab`, the container keeps it in the URL.
   */
  activeTab?: FollowUpsTab
  onSelectTab?: (tab: FollowUpsTab) => void
  /** Open the New follow-up dialog immediately (deep link / header menu). */
  startCreating?: boolean
  /** The create dialog closed (created or cancelled) — clear any URL flag. */
  onCreateClosed?: () => void
  /** Staff who can be assigned, passed through to the create dialog. */
  assigneeOptions?: Array<{ id: string; name: string }>
  /** Curated follow-up categories (from PocketBase), for picker and filter. */
  categoryOptions?: Array<{ id: string; name: string }>
  /** The signed-in user — enables the "Assigned to me" filter. */
  currentUserId?: string
  /**
   * Server paging for the non-due tabs. Without it (Storybook) those tabs
   * fall back to filtering `rows` client-side, as the due tab always does.
   */
  serverTab?: FollowUpsServerTab
  /** Total upcoming follow-ups for the tab label, counted server-side. */
  upcomingCount?: number
  /** Controlled "Assigned to me" — the container re-queries on toggle. */
  assignedToMe?: boolean
  onToggleAssignedToMe?: () => void
  /** Create a new customer from the dialog's search box. */
  onCreateCustomer?: (input: {
    fullName: string
    contactPhone?: string
    contactEmail?: string
  }) => Promise<{ id: string }> | { id: string }
  onCreate?: (
    input: NewFollowUpInput,
    extras: { firstNote?: string },
  ) => void
  /** Save edited values (title / due date / ladder / assignee) for a row. */
  onEdit?: (row: FollowUpRow, input: NewFollowUpInput) => void
  onComplete?: (id: string, options: { note?: string }) => void
  onSnooze?: (id: string, step: ScheduleStep, options: { note?: string }) => void
  /** A snooze to a picked date — moves the due date, leaves the ladder alone. */
  onReschedule?: (id: string, dueAt: string, options: { note?: string }) => void
  onClose?: (id: string, options: { note?: string }) => void
  onReopen?: (id: string, dueAt: string, options: { note?: string }) => void
  /** Hand the follow-up to a member of staff, from the side panel. */
  onAssign?: (id: string, userId: string) => void
  /** Add an answer to the follow-up's thread, from the side panel. */
  onAddNote?: (row: FollowUpRow, text: string) => boolean
  /** Add a photo answer (cropped + compressed) from the side panel. */
  onSubmitPhotoNote?: (row: FollowUpRow, input: PhotoNoteSubmit) => void
  /** Replace the image on one of the thread's photo notes. */
  onReplacePhotoNote?: (
    row: FollowUpRow,
    id: string,
    input: PhotoNoteReplace,
  ) => void
  /** Save an edited note body (text, or a photo note's caption). */
  onSaveEditNote?: (row: FollowUpRow, id: string, next: string) => void
  /** Soft-delete a note from the thread (kept as a tombstone). */
  onDeleteNote?: (row: FollowUpRow, id: string) => void
  /** Error to surface in the photo composer (e.g. a failed save). */
  photoNoteError?: string | null
  onClearPhotoNoteError?: () => void
  onOpenCustomer?: (row: FollowUpRow) => void
}

const dueTone: Record<DueState, LedgerBadgeTone> = {
  overdue: 'amber',
  today: 'blue',
  upcoming: 'slate',
}

/**
 * How a due date reads at a glance.
 *
 * Overdue is counted in days rather than shown as a date, because the number of
 * days something has been waiting is the part that decides whether to act now.
 */
export function dueLabel(dueAt: string, now: Date): string {
  const state = dueState({ dueAt }, now)
  if (state === 'today') return 'Today'

  const dueKey = toZaDateKey(dueAt)
  const todayKey = toZaNowKey(now)
  const days = daysBetween(todayKey, dueKey)

  if (state === 'overdue') {
    return days === 1 ? '1 day late' : `${days} days late`
  }
  if (days === 1) return 'Tomorrow'
  if (days <= 14) return `In ${days} days`
  return formatDayLabel(dueKey)
}

function toZaNowKey(now: Date): string {
  return toZaDateKey(now.toISOString())
}

/** "12 Aug" (year appended when it isn't this year) for the created-by line. */
function createdDateLabel(iso: string): string {
  return formatBookedLabel(fromUtcIsoToZaParts(iso).day, '')
}

function daysBetween(fromKey: string, toKey: string): number {
  const from = Date.parse(`${fromKey}T00:00:00Z`)
  const to = Date.parse(`${toKey}T00:00:00Z`)
  return Math.abs(Math.round((to - from) / 86_400_000))
}

function formatDayLabel(dateKey: string): string {
  const [year, month, day] = dateKey.split('-')
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${Number(day)} ${months[Number(month) - 1]} ${year}`
}

type PendingAction =
  | { kind: 'complete' | 'close'; row: FollowUpRow }
  | { kind: 'snooze'; row: FollowUpRow }
  | { kind: 'reopen'; row: FollowUpRow }

export function FollowUpsScreen({
  rows,
  activeTab,
  onSelectTab,
  startCreating = false,
  onCreateClosed,
  assigneeOptions,
  categoryOptions,
  currentUserId,
  serverTab,
  upcomingCount: upcomingCountProp,
  assignedToMe: assignedToMeProp,
  onToggleAssignedToMe,
  onCreateCustomer,
  onCreate,
  onEdit,
  onComplete,
  onSnooze,
  onReschedule,
  onClose,
  onReopen,
  onAssign,
  onAddNote,
  onSubmitPhotoNote,
  onReplacePhotoNote,
  onSaveEditNote,
  onDeleteNote,
  photoNoteError,
  onClearPhotoNoteError,
  onOpenCustomer,
}: FollowUpsScreenProps) {
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [isCreating, setIsCreating] = useState(startCreating)
  const [editing, setEditing] = useState<FollowUpRow | null>(null)
  const [localTab, setLocalTab] = useState<FollowUpsTab>('due')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  const [pendingAssign, setPendingAssign] = useState<FollowUpRow | null>(null)
  // Controlled by the container when server paging is on (the toggle scopes
  // the server queries too); local state keeps Storybook self-contained.
  const [localAssignedToMe, setLocalAssignedToMe] = useState(false)
  const assignedToMe = assignedToMeProp ?? localAssignedToMe
  const toggleAssignedToMe =
    onToggleAssignedToMe ?? (() => setLocalAssignedToMe((on) => !on))

  const closeCreateDialog = () => {
    setIsCreating(false)
    onCreateClosed?.()
  }

  // Navigating to ?new=1 while already on this page should still open it.
  useEffect(() => {
    if (startCreating) setIsCreating(true)
  }, [startCreating])

  const now = new Date()

  const tab = activeTab ?? localTab
  const selectTab = (next: FollowUpsTab) => {
    if (onSelectTab) {
      onSelectTab(next)
    } else {
      setLocalTab(next)
    }
  }

  const isDueNow = (row: FollowUpRow) => dueState(row, now) !== 'upcoming'
  // "Assigned to me" scopes everything — rows, tab counts, the side panel —
  // so the page reads as "my follow-ups" while it is on.
  const scopedRows =
    assignedToMe && currentUserId
      ? rows.filter((row) => row.assigneeId === currentUserId)
      : rows
  const openRows = scopedRows.filter((row) => row.status === 'open')
  const dueNowCount = openRows.filter(isDueNow).length
  // With server paging, `rows` holds only the synced due slice plus whatever
  // pages have been fetched — the upcoming total has to come from the server.
  const upcomingCount = serverTab
    ? upcomingCountProp
    : openRows.length - dueNowCount

  const serverMode = Boolean(serverTab) && tab !== 'due'

  // Completed follow-ups live on their own Archive tab; the working tabs
  // show open rows only. The due tab always reads the synced slice locally;
  // the other tabs follow the server search's membership + order when a
  // serverTab is wired, and fall back to client filtering when not.
  const rowsById = new Map(rows.map((row) => [row.id, row]))
  const visibleRows = serverMode
    ? serverTab!.keys.flatMap((key) => {
        // A key can transiently miss its record (e.g. a base-sync refetch
        // evicting an upserted row) until the next re-query settles.
        const row = rowsById.get(key)
        return row ? [row] : []
      })
    : tab === 'archive'
      ? scopedRows.filter((row) => row.status === 'closed')
      : openRows.filter((row) => (tab === 'due' ? isDueNow(row) : !isDueNow(row)))

  // Stable identity: LedgerTable re-fires its query effect when this changes,
  // so an inline closure would re-query on every render.
  const onServerQueryChange = serverTab?.onQueryChange
  const handleServerQuery = useCallback(
    (query: LedgerServerQuery) => {
      if (tab !== 'due') onServerQueryChange?.(tab, query)
    },
    [tab, onServerQueryChange],
  )

  // The side panel mirrors the Services page: clicking a row selects it, the
  // first visible row is selected by default so the panel is never empty.
  const selected =
    visibleRows.find((row) => row.id === selectedId) ??
    visibleRows[0] ??
    null

  const columns: LedgerColumn<FollowUpRow>[] = [
    {
      key: 'due',
      label: 'Due',
      widthClass: 'w-32',
      sortValue: (row) => row.dueAt,
      defaultSortDir: 'asc',
      render: (row) => (
        <LedgerBadge tone={dueTone[dueState(row, now)]}>
          {row.status === 'closed' ? 'Closed' : dueLabel(row.dueAt, now)}
        </LedgerBadge>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      widthClass: 'w-56',
      filter: 'text',
      filterValue: (row) => row.customerName,
      // Name columns only sort locally: the server search sorts on the
      // follow-up's own fields, and names live on other records there.
      sortValue: serverMode ? undefined : (row) => row.customerName,
      render: (row) => (
        <span className="block truncate text-slate-800">
          {row.customerName}
        </span>
      ),
    },
    {
      key: 'assignee',
      label: 'Assignee',
      widthClass: 'w-36',
      // A select on every tab: filters persist across tabs (same URL path),
      // and the server can only match an assignee exactly.
      filter: 'select',
      filterOptions: assigneeOptions?.length
        ? assigneeOptions.map((option) => ({
            value: option.name,
            label: option.name,
          }))
        : undefined,
      filterValue: (row) => row.assigneeName ?? '',
      sortValue: serverMode ? undefined : (row) => row.assigneeName ?? '',
      render: (row) =>
        row.assigneeName ? (
          <span className="truncate text-slate-700">{row.assigneeName}</span>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'category',
      label: 'Category',
      widthClass: 'w-32',
      filter: 'select',
      filterOptions: (categoryOptions ?? []).map((option) => ({
        value: option.name,
        label: option.name,
      })),
      filterValue: (row) => row.categoryName ?? '',
      sortValue: serverMode ? undefined : (row) => row.categoryName ?? '',
      render: (row) =>
        row.categoryName ? (
          <LedgerBadge tone="slate">{row.categoryName}</LedgerBadge>
        ) : (
          <span className="text-slate-400">—</span>
        ),
    },
    {
      key: 'title',
      label: 'Follow-up',
      filter: 'text',
      filterValue: (row) => row.title,
      render: (row) => (
        <div className="min-w-0 space-y-0.5">
          <div className="truncate text-slate-900">{row.title}</div>
          {row.service ? (
            <span className="text-[11px] text-slate-500">
              {row.service.label}
            </span>
          ) : null}
          {row.notes?.length ? (
            <div className="truncate text-[11px] text-violet-600">
              {row.notes.length} {row.notes.length === 1 ? 'note' : 'notes'}
              {' · '}
              <span className="text-slate-500">“{row.notes[0].text}”</span>
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'schedule',
      label: 'Repeats',
      widthClass: 'w-44',
      render: (row) => (
        <span className="text-[12px] text-slate-500">
          {describeSchedule(row.schedule)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      widthClass: 'w-52',
      render: (row) =>
        row.status === 'closed' ? (
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={(event) => {
              event.stopPropagation()
              setPending({ kind: 'reopen', row })
            }}
          >
            Reopen
          </button>
        ) : (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className={primaryButtonClass}
              onClick={(event) => {
                event.stopPropagation()
                setPending({ kind: 'complete', row })
              }}
            >
              Done
            </button>
            <button
              type="button"
              className={secondaryButtonClass}
              onClick={(event) => {
                event.stopPropagation()
                setPending({ kind: 'snooze', row })
              }}
            >
              Snooze
            </button>
            {onEdit ? (
              <button
                type="button"
                className={secondaryButtonClass}
                onClick={(event) => {
                  event.stopPropagation()
                  setEditing(row)
                }}
              >
                Edit
              </button>
            ) : null}
          </div>
        ),
    },
  ]

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="mx-auto max-w-[1720px] px-4 py-6">
        <nav
          aria-label="Follow-up tabs"
          className="mb-3 flex items-end gap-1 border-b border-slate-300"
        >
          {(
            [
              { key: 'due' as const, label: `Due now (${dueNowCount})` },
              {
                key: 'upcoming' as const,
                label:
                  upcomingCount === undefined
                    ? 'Upcoming'
                    : `Upcoming (${upcomingCount})`,
              },
              // No count: the archive is cold storage, rarely opened, and
              // not worth advertising a number for.
              { key: 'archive' as const, label: 'Archive' },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              className={
                tab === key
                  ? '-mb-px rounded-t-md border-x border-t border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-900'
                  : 'rounded-t-md border-x border-t border-transparent px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-200'
              }
              onClick={() => selectTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-start gap-4">
          <div className="min-w-0 flex-1">
        <LedgerTable
          // Remounting per tab resets page state and re-fires the server
          // query with the URL-restored filters for the incoming tab.
          key={tab}
          rows={visibleRows}
          columns={columns}
          rowKey={(row) => row.id}
          itemLabelPlural="follow-ups"
          title="Follow-ups"
          description="Reminders to get back to a customer. Marking one done schedules the next reminder — closing it is what stops them."
          emptyLabel={
            tab === 'due'
              ? 'Nothing due right now.'
              : tab === 'upcoming'
                ? 'Nothing scheduled ahead.'
                : 'Nothing archived yet.'
          }
          onRowClick={(row) => {
            setSelectedId(row.id)
            // Selecting a row is an explicit ask to see its thread —
            // re-expand the panel if it was collapsed.
            setPanelCollapsed(false)
          }}
          rowHighlight={(row) =>
            selected && row.id === selected.id
              ? 'bg-blue-50 shadow-[inset_3px_0_0_#8b5cf6]'
              : undefined
          }
          defaultSort={{ key: 'due', dir: tab === 'archive' ? 'desc' : 'asc' }}
          minTableWidthClass="min-w-[860px]"
          // Same shell as the Services/Customers ledgers: the viewport fills
          // to the bottom of the window and scrolls internally, so the page
          // itself never scrolls. The due tab keeps its whole (synced, small)
          // list in one scroll region; upcoming and archive grow without
          // bound, so they page through the server search instead.
          paginated={tab !== 'due'}
          initialPageSize={25}
          server={
            serverMode && serverTab
              ? {
                  totalItems: serverTab.totalItems,
                  loading: serverTab.loading,
                  onQueryChange: handleServerQuery,
                }
              : undefined
          }
          mobileSearchKey="customer"
          mobileCard={(row) => (
            <div className="space-y-1.5 px-3.5 py-3">
              {/* The side panel is desktop-only, so on a phone tapping the
                  card body keeps deep-linking into the customer's thread. */}
              <div
                onClick={(event) => {
                  event.stopPropagation()
                  onOpenCustomer?.(row)
                }}
                className="space-y-1.5"
              >
                <div className="flex items-center justify-between gap-2">
                  <LedgerBadge tone={dueTone[dueState(row, now)]}>
                    {row.status === 'closed'
                      ? 'Closed'
                      : dueLabel(row.dueAt, now)}
                  </LedgerBadge>
                  <span className="truncate text-[12px] text-slate-500">
                    {row.customerName}
                  </span>
                </div>
                <div className="text-[13px] text-slate-900">{row.title}</div>
                <div className="flex items-center gap-2 text-[11px]">
                  {row.assigneeName ? (
                    <span className="flex min-w-0 items-center gap-1">
                      <span className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-slate-200 text-[9px] font-semibold text-slate-600">
                        {initials(row.assigneeName)}
                      </span>
                      <span className="truncate text-slate-600">
                        {row.assigneeName}
                      </span>
                    </span>
                  ) : (
                    <span className="text-slate-400">Unassigned</span>
                  )}
                  <span className="text-slate-400">
                    · {describeSchedule(row.schedule)}
                  </span>
                </div>
                {row.notes?.length ? (
                  <div className="line-clamp-2 text-[11px] text-violet-600">
                    {row.notes.length}{' '}
                    {row.notes.length === 1 ? 'note' : 'notes'}
                    {' · '}
                    <span className="text-slate-500">“{row.notes[0].text}”</span>
                  </div>
                ) : null}
              </div>
              {row.status === 'closed' ? (
                <div className="pt-1">
                  <button
                    type="button"
                    className={secondaryButtonClass}
                    onClick={(event) => {
                      event.stopPropagation()
                      setPending({ kind: 'reopen', row })
                    }}
                  >
                    Reopen
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 pt-1">
                  <button
                    type="button"
                    className={primaryButtonClass}
                    onClick={(event) => {
                      event.stopPropagation()
                      setPending({ kind: 'complete', row })
                    }}
                  >
                    Done
                  </button>
                  <button
                    type="button"
                    className={secondaryButtonClass}
                    onClick={(event) => {
                      event.stopPropagation()
                      setPending({ kind: 'snooze', row })
                    }}
                  >
                    Snooze
                  </button>
                </div>
              )}
            </div>
          )}
          toggles={
            currentUserId ? (
              // Same shape and behaviour as the ledger's own "Show
              // completed" toggle, sitting right beside it.
              <button
                type="button"
                aria-pressed={assignedToMe}
                onClick={toggleAssignedToMe}
                className={[
                  'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border px-3 text-[13px] whitespace-nowrap',
                  assignedToMe
                    ? 'border-blue-200 bg-blue-50 font-medium text-blue-700'
                    : 'border-slate-300 bg-white text-slate-600',
                ].join(' ')}
              >
                <span className="text-sm leading-none">
                  {assignedToMe ? '☑' : '☐'}
                </span>
                Assigned to me
              </button>
            ) : null
          }
          actions={
            onCreate ? (
              <button
                type="button"
                className={primaryButtonClass}
                onClick={() => setIsCreating(true)}
              >
                New follow-up
              </button>
            ) : null
          }
        />
        {serverMode && serverTab?.error ? (
          <p className="mt-2 text-sm text-rose-600">
            Unable to load follow-ups right now. Adjust the search or try
            again.
          </p>
        ) : null}
          </div>

          {selected && panelCollapsed ? (
            <div className="sticky top-4 hidden w-[46px] shrink-0 flex-col items-center gap-3 self-start rounded-lg border border-slate-200 bg-white pt-3 pb-4 lg:flex">
              <button
                type="button"
                onClick={() => setPanelCollapsed(false)}
                title="Expand thread"
                className="h-[30px] w-[30px] cursor-pointer rounded-lg border border-slate-300 bg-white text-[15px] leading-none text-slate-700"
              >
                ‹
              </button>
              <div className="max-h-[60vh] overflow-hidden text-[11px] tracking-wide whitespace-nowrap text-ellipsis text-slate-500 [writing-mode:vertical-rl]">
                {selected.customerName} · {selected.title}
              </div>
            </div>
          ) : null}

          {selected && !panelCollapsed ? (
            // 174px = the chrome above the panel (header, page padding and
            // the tabs row — ~40px more than Services) plus the page's 24px
            // bottom padding, so a full panel bottoms out level with the
            // table instead of pushing the page into a scroll.
            <aside className="sticky top-4 hidden max-h-[calc(100dvh-174px)] w-96 shrink-0 flex-col self-start overflow-hidden rounded-lg border border-slate-200 bg-white lg:flex">
              {/* Header */}
              <div className="shrink-0 border-b border-slate-100 px-5 pt-[18px] pb-3.5">
                <div className="flex items-start justify-between gap-2.5">
                  <div className="min-w-0">
                    <div className="mb-2 flex items-center gap-2">
                      <LedgerBadge tone={dueTone[dueState(selected, now)]}>
                        {selected.status === 'closed'
                          ? 'Closed'
                          : dueLabel(selected.dueAt, now)}
                      </LedgerBadge>
                      {selected.service ? (
                        <span className="text-[11px] text-slate-500">
                          {selected.service.label}
                        </span>
                      ) : null}
                    </div>
                    <h2 className="m-0 text-base font-semibold">
                      <Link
                        to="/customers/$id"
                        params={{
                          id: selected.customerPublicId ?? selected.customerId,
                        }}
                        title="Open customer"
                        className="border-b border-blue-200 text-blue-700 hover:border-blue-700 hover:text-blue-800"
                      >
                        {selected.customerName}
                      </Link>
                    </h2>
                    <p className="m-0 text-[12.5px] leading-snug text-slate-500">
                      {selected.title}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    {onOpenCustomer ? (
                      <button
                        type="button"
                        onClick={() => onOpenCustomer(selected)}
                        title="Open the customer's thread"
                        className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-[7px] border border-slate-300 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Open
                        <span aria-hidden className="text-[10px]">
                          ↗
                        </span>
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setPanelCollapsed(true)}
                      title="Collapse thread"
                      className="h-7 w-7 cursor-pointer rounded-[7px] border border-slate-200 bg-white text-sm leading-none text-slate-500"
                    >
                      ›
                    </button>
                  </div>
                </div>
                {selected.details ? (
                  <p className="mt-2 mb-0 text-[12px] leading-snug text-slate-500">
                    {selected.details}
                  </p>
                ) : null}
              </div>

              {/* Actions + assignee */}
              <div className="flex shrink-0 flex-col gap-2.5 border-b border-slate-100 px-5 py-3.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[12px] text-slate-500">
                    {describeSchedule(selected.schedule)}
                  </span>
                  {selected.status === 'closed' ? (
                    <button
                      type="button"
                      className={secondaryButtonClass}
                      onClick={() =>
                        setPending({ kind: 'reopen', row: selected })
                      }
                    >
                      Reopen
                    </button>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        className={primaryButtonClass}
                        onClick={() =>
                          setPending({ kind: 'complete', row: selected })
                        }
                      >
                        Done
                      </button>
                      <button
                        type="button"
                        className={secondaryButtonClass}
                        onClick={() =>
                          setPending({ kind: 'snooze', row: selected })
                        }
                      >
                        Snooze
                      </button>
                      <button
                        type="button"
                        className={secondaryButtonClass}
                        onClick={() =>
                          setPending({ kind: 'close', row: selected })
                        }
                      >
                        Close
                      </button>
                    </div>
                  )}
                </div>
                {onAssign ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="shrink-0 text-[10px] tracking-wider text-slate-400 uppercase">
                        Assigned
                      </span>
                      <span
                        className={`truncate text-[12.5px] ${selected.assigneeName ? 'text-slate-900' : 'text-slate-400'}`}
                      >
                        {selected.assigneeName ?? 'Unassigned'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setPendingAssign(selected)}
                      className="h-[30px] shrink-0 cursor-pointer rounded-[7px] border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {selected.assigneeName ? 'Reassign…' : 'Assign…'}
                    </button>
                  </div>
                ) : null}
                {selected.createdByName ? (
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-[10px] tracking-wider text-slate-400 uppercase">
                      Created
                    </span>
                    <span className="truncate text-[12.5px] text-slate-700">
                      {selected.createdByName}
                      {selected.createdAt
                        ? ` · ${createdDateLabel(selected.createdAt)}`
                        : ''}
                    </span>
                  </div>
                ) : null}
              </div>

              {/* Contact details — this panel is where you act on a
                  follow-up, and acting usually means reaching the customer. */}
              <div className="grid shrink-0 grid-cols-2 gap-x-4 gap-y-3 border-b border-slate-100 px-5 py-3.5">
                <div className="min-w-0">
                  <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                    Phone
                  </div>
                  <div className="truncate text-[12.5px] text-slate-900">
                    {selected.customerPhone ? (
                      <a
                        href={`tel:${selected.customerPhone}`}
                        className="cursor-pointer text-blue-700 hover:underline"
                      >
                        {selected.customerPhone}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="mb-0.5 text-[10px] tracking-wider text-slate-400 uppercase">
                    Email
                  </div>
                  <div className="truncate text-[12.5px] text-slate-900">
                    {selected.customerEmail ? (
                      <a
                        href={`mailto:${selected.customerEmail}`}
                        title={selected.customerEmail}
                        className="cursor-pointer text-blue-700 hover:underline"
                      >
                        {selected.customerEmail}
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </div>
                </div>
              </div>

              {/* The thread. PanelNotes scrolls its feed internally, keeping
                  the composer pinned at the bottom of the panel. */}
              <div className="flex min-h-24 flex-1 flex-col overflow-hidden px-5 py-4">
                <PanelNotes
                  notes={selected.feed ?? []}
                  label="Notes"
                  emptyLabel="No notes yet."
                  onAddNote={
                    onAddNote
                      ? (text) => onAddNote(selected, text)
                      : undefined
                  }
                  onSubmitPhotoNote={
                    onSubmitPhotoNote
                      ? (input) => onSubmitPhotoNote(selected, input)
                      : undefined
                  }
                  onReplacePhotoNote={
                    onReplacePhotoNote
                      ? (id, input) => onReplacePhotoNote(selected, id, input)
                      : undefined
                  }
                  onSaveEditNote={
                    onSaveEditNote
                      ? (id, next) => onSaveEditNote(selected, id, next)
                      : undefined
                  }
                  onDeleteNote={
                    onDeleteNote
                      ? (id) => onDeleteNote(selected, id)
                      : undefined
                  }
                  photoError={photoNoteError}
                  onClearPhotoError={onClearPhotoNoteError}
                />
              </div>
            </aside>
          ) : null}
        </div>
      </main>

      {isCreating && onCreate ? (
        <NewFollowUpDialog
          assigneeOptions={assigneeOptions}
          categoryOptions={categoryOptions}
          onCreateCustomer={onCreateCustomer}
          onCancel={closeCreateDialog}
          onCreate={(input, extras) => {
            onCreate(input, extras)
            closeCreateDialog()
          }}
        />
      ) : null}

      {editing && onEdit ? (
        <NewFollowUpDialog
          mode="edit"
          initialCustomer={{ id: editing.customerId, name: editing.customerName }}
          assigneeOptions={assigneeOptions}
          categoryOptions={categoryOptions}
          initialValues={{
            title: editing.title,
            dueDay: toZaDateKey(editing.dueAt),
            schedule: remainingSchedule(editing),
            assigneeId: editing.assigneeId,
            categoryId: editing.categoryId,
          }}
          onCancel={() => setEditing(null)}
          onCreate={(input) => {
            onEdit(editing, input)
            setEditing(null)
          }}
        />
      ) : null}

      {pendingAssign && onAssign ? (
        <AssignUserDialog
          open
          refLabel={pendingAssign.customerName}
          contextLabel={pendingAssign.title}
          users={assigneeOptions ?? []}
          currentUserId={pendingAssign.assigneeId}
          onClose={() => setPendingAssign(null)}
          onConfirm={(userId) => {
            onAssign(pendingAssign.id, userId)
          }}
        />
      ) : null}

      {pending ? (
        <FollowUpActionDialog
          action={pending.kind}
          row={pending.row}
          onCancel={() => setPending(null)}
          onConfirm={(result) => {
            if (pending.kind === 'complete') {
              // Done with "don't remind again" ticked is the old Close.
              if (result.closeForGood) {
                onClose?.(pending.row.id, { note: result.note })
              } else {
                onComplete?.(pending.row.id, { note: result.note })
              }
            } else if (pending.kind === 'snooze' && result.step) {
              onSnooze?.(pending.row.id, result.step, { note: result.note })
            } else if (pending.kind === 'snooze' && result.dueAt) {
              onReschedule?.(pending.row.id, result.dueAt, {
                note: result.note,
              })
            } else if (pending.kind === 'close') {
              onClose?.(pending.row.id, { note: result.note })
            } else if (pending.kind === 'reopen' && result.dueAt) {
              onReopen?.(pending.row.id, result.dueAt, { note: result.note })
            }
            setPending(null)
          }}
        />
      ) : null}
    </div>
  )
}

const primaryButtonClass =
  'inline-flex h-8 items-center rounded-lg border border-blue-400 bg-blue-500 px-3 text-[12.5px] font-medium whitespace-nowrap text-white hover:bg-blue-600'
const secondaryButtonClass =
  'inline-flex h-8 items-center rounded-lg border border-slate-300 bg-white px-3 text-[12.5px] whitespace-nowrap text-slate-600 hover:bg-slate-50'

/** Storybook fixture: one of each state a follow-up can be in. */
export const demoFollowUps: FollowUpRow[] = [
  {
    id: 'fu1',
    customerId: 'c1',
    customerName: 'Marike van Wyk',
    title: 'Quote for the replacement SSD',
    dueAt: '2026-02-20T08:00:00.000Z',
    schedule: [],
    scheduleIndex: 0,
    status: 'open',
    service: { type: 'job', id: 'j1', label: 'Job J5H-Q4TV' },
  },
  {
    id: 'fu2',
    customerId: 'c2',
    customerName: 'Plett Beach Cafe',
    title: 'Check the till PC is still behaving',
    details: 'They had three lockups the week we rebuilt it.',
    dueAt: '2026-03-06T08:00:00.000Z',
    schedule: [
      { amount: 1, unit: 'month' },
      { amount: 6, unit: 'month' },
    ],
    scheduleIndex: 0,
    status: 'open',
  },
  {
    id: 'fu3',
    customerId: 'c3',
    customerName: 'Danie Koekemoer',
    title: 'Yearly backup check',
    dueAt: '2026-08-01T08:00:00.000Z',
    schedule: [{ amount: 1, unit: 'year' }],
    scheduleIndex: 0,
    status: 'open',
    lastDoneAt: '2025-08-01T08:00:00.000Z',
  },
  {
    id: 'fu4',
    customerId: 'c4',
    customerName: 'Suzanne Marais',
    title: 'Call back about the laptop hinge',
    dueAt: '2026-01-10T08:00:00.000Z',
    schedule: [],
    scheduleIndex: 0,
    status: 'closed',
  },
]
